import React, { FC, useMemo, useState } from 'react';

export interface ModalContextProps {
  open: boolean;
  showModal: (content: React.ReactNode) => void;
  hideModal: () => void;
  setContent: (content: React.ReactNode, useDefaultModalContainer: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
}

const ContentModalContext = React.createContext<ModalContextProps>({
  open: false,
  showModal: () => {},
  hideModal: () => {},
  setContent: () => {},
  openModal: () => {},
  closeModal: () => {},
});

export const ContentModalProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalState, setModalState] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>();
  const [useDefaultModalContainer, setUseDefaultModalContainer] = useState<boolean>(false);

  const showModal = (content: React.ReactNode, useDefaultModalContainer: boolean = false) => {
    setUseDefaultModalContainer(useDefaultModalContainer);
    setModalContent(content);
    setModalState(true);
  };

  const hideModal = () => {
    setModalContent(null);
    setModalState(false);
  };

  const openModal = () => {
    setModalState(true);
  };

  const closeModal = () => {
    setModalState(false);
  };

  const setContent = (content: React.ReactNode, useDefaultModalContainer: boolean = false) => {
    setUseDefaultModalContainer(useDefaultModalContainer);
    setModalContent(content);
  };

  const memoedValue = useMemo(() => {
    return {
      open: modalState,
      showModal,
      hideModal,
      setContent,
      openModal,
      closeModal,
    } as ModalContextProps;
  }, [modalState]);

  return (
    <ContentModalContext.Provider value={memoedValue}>
      {children}
      {/* eslint-disable-next-line no-nested-ternary */}
      {useDefaultModalContainer ? (
        <div className={`modal-container ${modalState ? 'active' : ''}`}>{modalContent}</div>
      ) : modalState ? (
        modalContent
      ) : null}
    </ContentModalContext.Provider>
  );
};

export default ContentModalContext;
